import jwt from './jwt';
import {logInTracking, logOutTracking} from './tracking';

/**
 * @typedef {Object} Endpoints
 * @property {string} jwks
 * @property {string} login
 * @property {string} logout
 * @property {string} metadata
 * @property {string} validate
 * @property {string} userinfo
 * @property {string} cms_login
 * @property {string} cms_logout
 * @property {string} cms_status
 */

/**
 * @typedef {Object} AuthConfig
 * @property {string} baseApi
 * @property {string} siteHandle
 * @property {string} currentLang
 * @property {Endpoints} endpoints
 */

/**
 * @typedef {Object} Metadata
 * @property {MetadataUser} metadata
 */

/**
 * @typedef {Object} MetadataUser
 * @property {string} metadata
 * @property {string} address_line_1
 * @property {string} address_line_2
 * @property {string} admeira_id
 * @property {string} birth_year
 * @property {string} city
 * @property {string} company
 * @property {string} country
 * @property {string} email
 * @property {boolean} email_only
 * @property {string} first_name
 * @property {string} gender
 * @property {string} identityToken
 * @property {string} initials
 * @property {string} lang
 * @property {string} last_name
 * @property {Object} s
 * @property {string} sub
 * @property {string} unhashed_sub
 * @property {string} zip_code
 */

const EVENT = {
  INIT: 'auth:init',
  LOGIN: 'auth:login',
  LOGOUT: 'auth:logout',
  VALIDATE: 'auth:validate',
  ERROR: 'auth:error',
};

const STORAGE = {
  TOKEN: 'rToken',
  LOGGED_IN: 'userLoggedIn',
};

const SELECTOR = {
  LOGIN_BTN: '.js-action-login',
  LOGOUT_BTN: '.js-action-logout',
};

export default class AuthClient {

  /**
   * @param {AuthConfig} config
   */
  constructor(config) {
    this.config = config;
    this.eventBus = document.createElement('div');
    this.sessionKey = STORAGE.TOKEN  + `_${config.siteHandle}`;
    console.log(this.sessionKey)
    this.loginButtonSelector = '.js-action-login';
    this.logoutButtonSelector = '.js-action-logout';

    this.initEventListeners();
  }

  emit(eventName, detail = {}) {
    this._log('bus', 'emit', eventName);
    const event = new CustomEvent(eventName, {detail});
    this.eventBus.dispatchEvent(event);
  }

  initEventListeners() {
    window.addEventListener('DOMContentLoaded', (e) => this.initializeUiEvents(e));
    document.addEventListener('htmx:afterSwap', (event) => {
      if (event.srcElement.id != 'voting-sprig' ) {
        return;
      }
      const loginButtons = [...event.srcElement.querySelectorAll(SELECTOR.LOGIN_BTN)];
      for (const loginButton of loginButtons) {
        loginButton?.addEventListener('click', (e) => this.handleLoginClick(e), false);
      }
    });
    this.eventBus.addEventListener(EVENT.LOGIN, (e) => this.handleLogin(e));
    this.eventBus.addEventListener(EVENT.INIT, (e) => this.handleInit(e));
    this.eventBus.addEventListener(EVENT.VALIDATE, (e) => this.handleValidate(e));
    this.eventBus.addEventListener(EVENT.LOGOUT, (e) => this.handleLogout(e));
    this.eventBus.addEventListener(EVENT.ERROR, (e) => this.handleError(e));
  }

  initializeUiEvents() {
    const loginButtons = [...document.querySelectorAll(SELECTOR.LOGIN_BTN)];
    const logoutButton = document.querySelector(SELECTOR.LOGOUT_BTN);

    for (const loginButton of loginButtons) {
      loginButton?.addEventListener('click', (e) => this.handleLoginClick(e), false);
    }

    if (logoutButton) {
      logoutButton.addEventListener('click', (e) => this.handleLogoutClick(e), false);
    }


  }

  handleLoginClick() {
    this.emit(EVENT.LOGIN);
  }

  handleLogoutClick() {
    console.log('emit logout')
    this.emit(EVENT.LOGOUT);
  }

  handleLogin() {
    this._log('login', 'Handling');
    const endpointUrl = this._buildEndpoint('login');
    endpointUrl.searchParams.append('state', JSON.stringify({redirectUri: window.location.href}));
    endpointUrl.searchParams.append('login_case', 'email_only');
    endpointUrl.searchParams.append('lang', this.config.currentLang);
    endpointUrl.searchParams.append('source', this.config.siteHandle);
    endpointUrl.searchParams.append('redirect_uri', window.location.href);
    this._log('login', endpointUrl.href);
    window.location.href = endpointUrl.href;
  }

  async handleInit() {
    this._log('init', 'Starting authentication check');

    const status = await this._getCmsStatus();
    if (status.loggedIn && status.skipAuthSync) {
      this._log('init', 'Trusted CMS session, skipping Ringier sync');
      sessionStorage.setItem(STORAGE.LOGGED_IN, '1');
      return;
    }

    const existingToken = sessionStorage.getItem(this.sessionKey);

    if (existingToken) {
      this.emit(EVENT.VALIDATE, {token: existingToken, source: 'session'});
      return;
    }

    this._log('init', 'No token found, check metadata');
      try {
        const data = await this.getUserMetadata();

        if (!data?.identityToken) {
        this._error('init', 'userinfo returned no identityToken');
        await this._forceLogout('no_identity_token');
        return;
        }

      this._log('init', 'Received token from 3rd party');
        const token = data.identityToken;

        const isValidJWT = await this.validateJWTSignature(token);

        if (!isValidJWT) {
        this._error('init', 'JWT signature validation failed');
        this.emit(EVENT.ERROR, {reason: 'invalid_jwt', action: 'redirect_login'});
          return;
        }
      this._log('init', 'JWT signature valid');

      sessionStorage.setItem(this.sessionKey, token);

      this.emit(EVENT.VALIDATE, {token, source: 'metadata'});
      } catch (error) {
      this._error('init', 'Error during initialization:', error);
      await this._forceLogout('init_failed');
    }
  }

  async handleValidate(event) {
    const {token, source} = event.detail;

    try {
      const validateEndpoint = this._buildEndpoint('cms_login');

      try {
        await this.getUserMetadata();
      } catch (err) {
        this._error('validate', 'Remote session invalid:', err);
        await this._forceLogout('remote_session_invalid');
        return;
      }

      const response = await fetch(validateEndpoint, {
        method: 'POST',
        headers: {'Authorization': 'Bearer ' + token},
      });

      // A rejected token now comes back as 401, not 500. Either way the stored token is
      // no good, so drop the session rather than retrying with it.
      if (response.status === 401 || response.status === 500) {
        this._error('validate', 'Login JWT rejected, reloading');
        await this._forceLogout('cms_login_rejected');
        return;
      }

      if (!response.ok) {
        throw new Error(`Validation failed: ${response.status}`);
      }

      const result = await response.json();
      const loggedInFlag = sessionStorage.getItem(STORAGE.LOGGED_IN);

      if (source === 'metadata' || loggedInFlag !== '1') {
        sessionStorage.setItem(STORAGE.LOGGED_IN, '1');
        logInTracking();
        window.location.reload();
      } else if (source === 'session') {
        sessionStorage.setItem(STORAGE.LOGGED_IN, '1');
        this._log('validate', 'session reuse ok', result?.id);
      }
    } catch (error) {
      this._error('validate', 'Validation error:', error);
      this._clearSession();
    }
  }

  async handleLogout() {
    this._log('logout', 'Starting logout process');

    logOutTracking();
    const response = await fetch(this._buildEndpoint('cms_logout'),
      {
        method: 'POST',
      credentials: 'include',
        keepalive: true,
      });

    if (!response.ok) {
      this._error('logout', 'cms_logout returned', response.status);
    } else {
      const result = await response.json();
      this._log('logout', 'cms_logout ok', result);
    }

    this._clearSession();

    const logoutUrl = new URL(this._buildEndpoint('endsession'));
    const returnToUrl = new URL(this._buildEndpoint('logout'));

    returnToUrl.searchParams.append('redirectUri', window.location.href);
    returnToUrl.searchParams.append('lang', this.config.currentLang);
    logoutUrl.searchParams.append('returnTo', returnToUrl.href);
    logoutUrl.searchParams.append('lang', this.config.currentLang);

    window.location.href = logoutUrl.href;
  }

  handleError(ev) {
    const {reason, error, action} = ev.detail;

    this._error('handleError', reason, error);

    if (action === 'logout') {
      this.emit(EVENT.LOGOUT);
    }
  }

  /**
   * @returns {Promise<Metadata>}
   */
  async getUserMetadata() {
    const endpointUrl = this._buildEndpoint('userinfo');

    const response = await fetch(endpointUrl.href, {credentials: 'include'});

    if (response.status === 401) {
      throw new Error('[auth:init] User not authenticated with 3rd party');
    }

    if (!response.ok) {
      throw new Error(`[auth:init] Metadata fetch failed ${response.status}`);
    }

    return await response.json();
  }

  _buildEndpoint(identifier) {
    if (identifier === 'cms_login' || identifier === 'cms_logout' || identifier === 'cms_status') {
      return this.config.endpoints[identifier];
    }
    const base = this.config.baseApi.replace(/\/$/, '');
    const path = this.config.endpoints[identifier].replace(/^\//, '');
    return new URL(`${base}/${path}`);
  }

  async validateJWTSignature(token) {

    const endpoint = this._buildEndpoint('jwks');
    try {
      return await jwt.validate(endpoint, token);
    } catch (error) {
      this._error('jwt', error);
      return false;
    }
  }

  _clearSession() {
    sessionStorage.removeItem(this.sessionKey);
    sessionStorage.removeItem(STORAGE.LOGGED_IN);
  }

  async _forceLogout(reason) {
    this._log('forceLogout', reason);
    this._clearSession();

    const status = await this._getCmsStatus();

    if (status.skipAuthSync) {
      this._log('forceLogout', 'trusted CMS session, skipping logout flow');
      return;
    }

    if (!status.loggedIn) {
      this._log('forceLogout', 'cms already logged out, skipping logout flow');
      return;
    }
    await this.handleLogout();
  }

  /**
   * @returns {Promise<{loggedIn: boolean, skipAuthSync: boolean}>}
   */
  async _getCmsStatus() {
    try {
      const response = await fetch(this._buildEndpoint('cms_status'), {
        credentials: 'include',
      });
      if (!response.ok) {
        return {loggedIn: false, skipAuthSync: false};
      }
      const data = await response.json();
      return {
        loggedIn: data?.loggedIn === true,
        skipAuthSync: data?.skipAuthSync === true,
      };
    } catch (error) {
      this._error('status', error);
      return {loggedIn: false, skipAuthSync: false};
    }
  }

  _log(scope, ...args) {
    console.log(`[auth:${scope}]`, ...args);
  }

  _error(scope, ...args) {
    console.error(`[auth:${scope}]`, ...args);
    }
  }
